/*
 * The sending mailbox's signature in the reply box (docs/2026-11-03-reply-box-mailbox-signature.md).
 * Email Bison adds none to a reply, so the box shows it in the words it sends, as a mail client does:
 * already there when the box opens empty, gone when a paste replaces everything, and back with one
 * click. A box holding only the signature holds nothing of the rep's: Send waits and nothing is kept.
 *
 * Matching is on the words alone, spaces and case aside, so the same signature read from the editor's
 * paragraphs or from a textarea's lines is the same signature.
 */

const flat = (text: string): string => text.replace(/\s+/gu, " ").trim().toLowerCase();

/** The box's own words: its text without the signature it ends with, if it ends with it. */
export function ownWords(text: string, signature: string | null): string {
  const target = signature === null ? "" : flat(signature);
  if (target === "") return text.trim();
  const lines = text.split("\n");
  for (let cut = lines.length - 1; cut >= 0; cut -= 1) {
    const tail = flat(lines.slice(cut).join(" "));
    if (tail === target) return lines.slice(0, cut).join("\n").trim();
    if (tail.length > target.length) break;
  }
  return text.trim();
}

/** Whether the text ends with the signature. */
export function endsWithSignature(text: string, signature: string | null): boolean {
  if (signature === null || flat(signature) === "") return false;
  return flat(text).endsWith(flat(signature)) && ownWords(text, signature) !== text.trim();
}

/** Plain text with the signature after it, a blank line between; on its own, a blank line above it. */
export function withSignature(text: string, signature: string): string {
  const words = text.trimEnd();
  return words.trim() === "" ? `\n\n${signature}` : `${words}\n\n${signature}`;
}

/**
 * Where the signature starts among a Formatted document's top-level blocks, reading back from the
 * end: the position of the first block of the run whose words are the signature's, or null. `text`
 * reads a range the way the box's plain words are read, a line break as a line break.
 */
export function signatureStart(
  blocks: readonly { readonly from: number }[],
  end: number,
  text: (from: number, to: number) => string,
  signature: string,
): number | null {
  const target = flat(signature);
  if (target === "") return null;
  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    const block = blocks[index];
    if (!block) continue;
    const tail = flat(text(block.from, end));
    if (tail === target) return block.from;
    if (tail.length > target.length) return null;
  }
  return null;
}
